import {createSlice} from '@reduxjs/toolkit'
import AuthService from '../../api/auth'
import CustomerService from '../../api/customer'
import localStorageManager, {
    getCurrentTest,
    getPreviousTestAlerts,
    setCustomerData
} from '../../utils/localStorageManager'
import {setCustomerData as resetCustomerData} from '../../stores/sidebar'
import {setConfirmIsActive} from '../alert'
import FormHelper from '../../utils/forms'
import QueryHelper from '../../utils/query'
import {routes} from '../../routes'
import {toast} from 'react-toastify'
import channelTypes from '../../data/channelTypes.json'
import localStorageKeys from '../../utils/localStorageKeys'
import {getAllLimitUsages, userLogOut} from '../auth'
import Customer from '../../utils/customer'
import NavigationHelper from '../../utils/navigation'

export const settingsSlice = createSlice({
    name: 'customer_settings',
    initialState: {
        buttonIsDisabled: true,
        showLoader: false,
        isFetching: true,
        personalChannels: [],
        addedChannel: {},
        checkedChannels: [],
        isDeleting: false
    },
    reducers: {
        setButtonIsDisabled: (state, {payload}) => {
            state.buttonIsDisabled = payload
        },
        setShowLoader: (state, {payload}) => {
            state.showLoader = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setPersonalChannels: (state, {payload}) => {
            state.personalChannels = payload
        },
        setAddedChannel: (state, {payload}) => {
            state.addedChannel = payload
        },
        setCheckedChannels: (state, {payload}) => {
            state.checkedChannels = payload
        },
        setIsDeleting: (state) => {
            state.isDeleting = !state.isDeleting
        }
    }
})

export const {
    setButtonIsDisabled,
    setShowLoader,
    setIsFetching,
    setPersonalChannels,
    setAddedChannel,
    setCheckedChannels,
    setIsDeleting
} = settingsSlice.actions

export default settingsSlice.reducer

export const updateCustomer = (formData, setFieldError) => {
    return async dispatch => {
        dispatch(setShowLoader(true))
        try {
            const {data} = await CustomerService.updateCustomerData(formData)
            toast.success(data?.message)
            const customer = localStorageManager.get('customer')
            customer.name = formData.name
            customer.surname = formData.surname
            setCustomerData(customer)
            dispatch(resetCustomerData())
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setShowLoader(false))
    }
}

export const updatePassword = (formData, setFieldError) => {
    return async dispatch => {
        dispatch(setShowLoader(true))
        try {
            const {data} = await AuthService.updateCustomerPassword(formData)
            toast.success(data?.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setShowLoader(false))
    }
}

export const updateCustomerUsername = (formData, setFieldError, navigate) => {
    return async dispatch => {
        dispatch(setShowLoader(true))
        try {
            const {data} = await CustomerService.updateUsername(formData)
            toast.success(data?.message)
            sessionStorage.setItem('requested_email', formData.email)
            navigate(NavigationHelper.getLocalizedPath(routes.confirm_code.paths))
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setShowLoader(false))
    }
}

export const codeConfirmation = (formData, setFieldError, resetForm, navigate) => {
    return async dispatch => {
        dispatch(setShowLoader(true))
        try {
            const {data} = await CustomerService.confirmCode(formData)
            toast.success(data?.message)
            const customer = localStorageManager.get('customer')
            customer.email = sessionStorage.getItem('requested_email')
            setCustomerData(customer)
            resetForm()
            navigate(NavigationHelper.getLocalizedPath(routes.security.paths))
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
        dispatch(setShowLoader(false))
    }
}

export const getPersonalNotificationChannels = (isTeamChannel, showLoader = true, customerId) => {
    return async(dispatch, getState) => {
        showLoader && dispatch(setIsFetching(true))
        const teamId = Customer.isRoot() ? getState().teams?.selectedTeamId : localStorageManager.get(localStorageKeys.TEAM).team_id
        try {
            const channelsData = isTeamChannel ?
                await CustomerService.getTeamNotificationChannels(teamId) :
                await CustomerService.getPersonalNotificationChannels(customerId)
            dispatch(setPersonalChannels(channelsData?.data?.data?.channels || []))
        } catch (e) {
            toast.error(e?.response?.data?.message)
        } finally {
            showLoader && dispatch(setIsFetching(false))
        }
    }
}

export const updateChannelStatus = (isTeamChannel, id, formData) => {
    return async(dispatch) => {
        try {
            const {data} = isTeamChannel ?
                await CustomerService.updateTeamNotificationChannelStatus(id, formData) :
                await CustomerService.updatePersonalNotificationChannelStatus(id, formData)
            dispatch(getAllLimitUsages())
            toast.success(data?.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

export const updateChannel = (isTeamChannel, id, formData, setFieldError) => {
    return async() => {
        try {
            const {data} = isTeamChannel ?
                await CustomerService.updateTeamNotificationChannel(id, formData) :
                await CustomerService.updatePersonalNotificationChannel(id, formData)
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const addChannel = (isTeamChannel, id, formData, setFieldError, resetForm = () => {
}) => {
    return async dispatch => {
        try {
            const {data} = isTeamChannel ?
                await CustomerService.addTeamNotificationChannel(id, formData) :
                await CustomerService.addPersonalNotificationChannel(id, formData)
            toast.success(data?.message)
            if (formData.type !== channelTypes.types.slack && formData.type !== channelTypes.types.discord) {
                dispatch(setAddedChannel(data.data.channel))
                resetForm()
                return true
            }
            dispatch(getPersonalNotificationChannels(true))
            dispatch(getAllLimitUsages())
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const verifyNotificationCode = (isTeamChannel, id, formData, setFieldError) => {
    return async(_, getState) => {
        try {
            const teamId = Customer.isRoot() ? getState().teams?.selectedTeamId : localStorageManager.get(localStorageKeys.TEAM).team_id
            const {data} = isTeamChannel ?
                await CustomerService.verifyTeamNotificationChannel(teamId, {
                    ...formData,
                    code: String(formData.code)
                }) :
                await CustomerService.verifyPersonalNotificationChannel(id, {
                    ...formData,
                    code: String(formData.code)
                })
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message, setFieldError)
        }
    }
}

export const deleteChannels = (isTeamChannel, id, priorities, customerId) => {
    return async dispatch => {
        dispatch(setIsDeleting())
        try {
            const query = QueryHelper.makeQueryFromArray('priority', priorities)
            const {data} = isTeamChannel ?
                await CustomerService.deleteTeamNotificationChannel(id, query) :
                await CustomerService.deletePersonalNotificationChannel(id, query)
            dispatch(getPersonalNotificationChannels(isTeamChannel, false, customerId))
            dispatch(setCheckedChannels([]))
            dispatch(getAllLimitUsages())
            toast.success(data?.message)
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
        dispatch(setIsDeleting())
        dispatch(setConfirmIsActive(false))
    }
}

export const logUpgradePlanNavigation = () => {
    return async() => {
        try {
            await CustomerService.logAction({
                action: 'Navigated to upgrade plan page'
            })
        } catch (e) {
            toast.error(e?.response?.data?.message)
        }
    }
}

export const logCustomerIsActive = () => {
    return async() => {
        try {
            await CustomerService.logAction({
                action: 'customer-is-active'
            })
            return true
        } catch (e) {
            return false
        }
    }
}

export const deleteAccountPermanently = data => {
    return async(dispatch) => {
        try {
            const response = await CustomerService.deleteAccount(data)
            toast.success(response?.message)
            dispatch(userLogOut())
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}

const getPreviousTests = (previousTestAlerts, currentTest) => {
    const index = previousTestAlerts.findIndex(item => item?.priority === currentTest?.priority && item?.isTeamChannel === currentTest?.isTeamChannel)
    if (index > -1) {
        previousTestAlerts[index] = currentTest
    } else {
        previousTestAlerts.push(currentTest)
    }
    return previousTestAlerts
}

export const sendTestAlert = (isTeamChannel, id, formData) => {
    return async() => {
        try {
            const previousTestAlerts = getPreviousTestAlerts()
            const currentTest = getCurrentTest(formData, isTeamChannel) || {
                count: 0,
                isTeamChannel,
                priority: formData?.priority,
                date: new Date()
            }

            currentTest.count = currentTest.count + 1
            localStorageManager.set(localStorageKeys.tests, getPreviousTests(previousTestAlerts, currentTest))

            const {data} = isTeamChannel ?
                await CustomerService.sendTeamChannelTestAlert(id, formData) :
                await CustomerService.sendPersonalChannelTestAlert(id, formData)
            toast.success(data?.message)
            return true
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message
            FormHelper.setFormElementError(fields, message)
        }
    }
}
